import React, { Component } from 'react';
import { arrayOf, bool, object, func, shape, string } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { Switch, Route, withRouter, Redirect } from 'react-router-dom';
import { useRouteConfiguration } from '../context/routeConfigurationContext';
import { propTypes } from '../util/types';
import * as log from '../util/log';
import { canonicalRoutePath, createResourceLocatorString } from '../util/routes';
import { useConfiguration } from '../context/configurationContext';
import { locationChanged } from '../ducks/routing.duck';
import { NamedRedirect } from '../components';
import NotFoundPage from '../containers/NotFoundPage/NotFoundPage';
import LoadableComponentErrorBoundary from './LoadableComponentErrorBoundary/LoadableComponentErrorBoundary';
import { canAccessAI, hasPremiumContentAccess } from '../util/dataExtractors';
import { isRootPath, getDefaultLocale } from '../util/genericHelpers';
import { getElementForHash } from '../util/scrollToFragment';
import { getUrlForLocale } from '../helpers/convert';

const logPaywallDecision = payload => {
  if (process.env.REACT_APP_ENV === 'development') {
    // eslint-disable-next-line no-console
    console.debug('[PaywallGuard]', payload);
  }
};

const canShowComponent = props => {
  const { isAuthenticated, route } = props;
  const { auth } = route;
  return !auth || isAuthenticated;
};
const callLoadData = props => {
  const { match, location, route, dispatch, logoutInProgress, config } = props;
  const { loadData, name } = route;
  const shouldLoadData =
    typeof loadData === 'function' && canShowComponent(props) && !logoutInProgress;
  if (shouldLoadData) {
    dispatch(loadData(match.params, location.search, config, location?.state))
      .then(() => {
        if (props.logLoadDataCalls) {
          // This gives good input for debugging issues on live environments, but with test it's not needed.
        }
      })
      .catch(e => {
        log.error(e, 'load-data-failed', { routeName: name });
      });
  }
};
const setPageScrollPosition = (location, delayed) => {
  if (!location.hash) {
    // No hash, scroll to top
    window.scroll({
      top: 0,
      left: 0,
    });
  } else {
    const el = getElementForHash(location.hash);
    if (el) {
      // Found element from the current page with the given fragment identifier,
      // scrolling to that element.
      //
      // NOTE: This only works on in-app navigation within the same page.
      // If smooth scrolling is needed between different pages, one needs to wait
      //   1. loadData fetch and
      //   2. code-chunk fetch
      // before making el.scrollIntoView call.
      el.scrollIntoView({
        block: 'start',
        behavior: 'smooth',
      });
    } else {
      // A naive attempt to make a delayed call to scrollIntoView
      // Note: 300 milliseconds might not be enough, but adding too much delay
      // might affect user initiated scrolling.
      delayed = window.setTimeout(() => {
        const reTry = getElementForHash(location.hash);
        reTry?.scrollIntoView({
          block: 'start',
          behavior: 'smooth',
        });
      }, 300);
    }
  }
};
const handleLocationChanged = (dispatch, location, routeConfiguration, delayed) => {
  setPageScrollPosition(location, delayed);
  const path = canonicalRoutePath(routeConfiguration, location);
  dispatch(locationChanged(location, path));
};
/**
 * RouteComponentRenderer handles loadData calls on client-side.
 * It also checks authentication and redirects unauthenticated users
 * away from routes that are for authenticated users only
 * (aka "auth: true" is set in routeConfiguration.js)
 *
 * This component is a container: it needs to be connected to Redux.
 */
class RouteComponentRenderer extends Component {
  componentDidMount() {
    const { dispatch, location, routeConfiguration } = this.props;
    this.delayed = null;
    // Calling loadData on initial rendering (on client side).
    callLoadData(this.props);
    handleLocationChanged(dispatch, location, routeConfiguration, this.delayed);
  }
  componentDidUpdate(prevProps) {
    const { dispatch, location, routeConfiguration } = this.props;
    // Call for handleLocationChanged affects store/state
    // and it generates an unnecessary update.
    if (prevProps.location !== this.props.location) {
      // Calling loadData after initial rendering (on client side).
      // This makes it possible to use loadData as default client side data loading technique.
      // However it is better to fetch data before location change to avoid "Loading data" state.
      callLoadData(this.props);
      handleLocationChanged(dispatch, location, routeConfiguration, this.delayed);
    }
  }
  componentWillUnmount() {
    if (this.delayed) {
      window.clearTimeout(this.resetTimeoutId);
    }
  }
  render() {
    const { route, match, location, staticContext, isAuthenticated, currentUser } = this.props;
    const { component: RouteComponent, authPage = 'LoginPage', extraProps } = route;
    const canShow = canShowComponent(this.props);
    if (!canShow) {
      staticContext.unauthorized = true;
    }
    // Function to handle locale initialization
    const initializeLocale = () => {
      // Retrieve the current locale and the flag from localStorage
      const currentLocale = window.localStorage.getItem('locale');
      const isDefaultLocaleSet = window.localStorage.getItem('default_locale_set');
      if (!isDefaultLocaleSet) {
        window.localStorage.setItem('locale', 'en');
        window.localStorage.setItem('default_locale_set', 'true'); // Mark the default locale as set
      }
    };
    if (typeof window !== 'undefined') {
      // Call the function before rendering
      initializeLocale();
    }
    if (isAuthenticated && isRootPath(location?.pathname)) {
      const { routeConfiguration } = this.props;
      const pathParts = location.pathname.split('/').filter(Boolean);
      const pathLocale = pathParts[0] || null;
      const SUPPORTED_LOCALES = ['en', 'de', 'el', 'ar'];
      const currentLocale = (pathLocale && SUPPORTED_LOCALES.includes(pathLocale)) 
        ? pathLocale 
        : getDefaultLocale();
      const baseHomepagePath = createResourceLocatorString('Homepage', routeConfiguration, {}, {});
      const homepagePath = getUrlForLocale(baseHomepagePath, currentLocale);
      
      return (
        <Redirect
          to={{
            pathname: homepagePath,
            search: location.search,
            hash: location.hash,
            state: { from: `${location.pathname}${location.search}${location.hash}` },
          }}
        />
      );
    }
    if (route.requiresTrial && isAuthenticated) {
      if (!currentUser?.id) {
        logPaywallDecision({
          routeName: route?.name,
          pathname: location?.pathname,
          resolved: false,
          reason: 'current_user_not_loaded',
        });
        return null;
      }

      const isAiRoute = route?.name === 'AIConsultPage' || route?.name === 'MobileAIConsultPage';
      const allowAccess = isAiRoute
        ? canAccessAI(currentUser)
        : hasPremiumContentAccess(currentUser);
      logPaywallDecision({
        routeName: route?.name,
        pathname: location?.pathname,
        resolved: true,
        allowAccess,
      });

      if (!allowAccess) {
        return (
          <NamedRedirect
            name="PaywallPage"
            state={{ from: `${location.pathname}${location.search}${location.hash}` }}
          />
        );
      }
    }
    return canShow ? (
      <LoadableComponentErrorBoundary>
        <RouteComponent
          params={match.params}
          location={location}
          {...extraProps}
          scrollingDisabled={this.props.scrollingDisabled}
          viewport={this.props.viewport}
        />
      </LoadableComponentErrorBoundary>
    ) : (
      <NamedRedirect
        name={authPage}
        state={{ from: `${location.pathname}${location.search}${location.hash}` }}
        search={`?from=booking${location.search}&userType=user`}
      />
    );
  }
}
RouteComponentRenderer.defaultProps = { staticContext: {} };
RouteComponentRenderer.propTypes = {
  isAuthenticated: bool.isRequired,
  logoutInProgress: bool.isRequired,
  route: propTypes.route.isRequired,
  routeConfiguration: arrayOf(propTypes.route).isRequired,
  match: shape({
    params: object.isRequired,
    url: string.isRequired,
  }).isRequired,
  location: shape({
    search: string.isRequired,
  }).isRequired,
  staticContext: object,
  dispatch: func.isRequired,
};
const mapStateToProps = state => {
  const { isAuthenticated, logoutInProgress } = state.auth;
  const { currentUser } = state.user;
  const { scrollingDisabled, viewport } = state.ui;
  return { isAuthenticated, logoutInProgress, currentUser, scrollingDisabled, viewport };
};
const RouteComponentContainer = compose(connect(mapStateToProps))(RouteComponentRenderer);
/**
 * Routes component creates React Router rendering setup.
 * It needs routeConfiguration (named as "routes") through props.
 * Using that configuration it creates navigation on top of page-level
 * components. Essentially, it's something like:
 * <Switch>
 *   <Route render={pageA} />
 *   <Route render={pageB} />
 * </Switch>
 */
const Routes = (props, context) => {
  const routeConfiguration = useRouteConfiguration();
  const config = useConfiguration();
  const { isAuthenticated, logoutInProgress } = props;
  const toRouteComponent = route => {
    const renderProps = {
      isAuthenticated,
      logoutInProgress,
      route,
      routeConfiguration,
      config,
    };
    // By default, our routes are exact.
    // https://reacttraining.com/react-router/web/api/Route/exact-bool
    const isExact = route.exact != null ? route.exact : true;
    return (
      <Route
        key={route.name}
        path={route.path}
        exact={isExact}
        render={matchProps => (
          <RouteComponentContainer
            {...renderProps}
            match={matchProps.match}
            location={matchProps.location}
            staticContext={matchProps.staticContext}
            scrollingDisabled={props.scrollingDisabled}
          />
        )}
      />
    );
  };
  return (
    <Switch>
      {routeConfiguration.map(toRouteComponent)}
      <Route component={NotFoundPage} />
    </Switch>
  );
};
export default withRouter(connect(mapStateToProps)(Routes));
